// src/App.jsx
/**
 * Application entry point.
 *
 * Responsibilities
 * ────────────────
 *  • Mount the two possible nav variants (`<NavBar/> | <MDNavBar/>`).
 *  • Gate protected routes behind `<ProtectedRoute/>`.
 *  • Lazy-load page modules to keep initial bundle size low.
 *  • Redirect unmatched paths to `/`.
 *  • Run a quick device-capability check (`useDeviceCompatibility`). On
 *    unsupported devices we replace the `/auth/*` subtree with a
 *    compatibility-warning page.
 *
 * Layout skeleton
 *  <div class="h-[100dvh] flex flex-col">   ← fills viewport height
 *    {NavBar}
 *    <Suspense>  ← lazy pages rendered inside
 *       <Routes> … </Routes>
 *    </Suspense>
 *  </div>
 *
 * Notes
 *  • Use `100dvh` (dynamic viewport unit) so Safari iOS 15+ doesn’t crop
 *    the content when the URL bar shrinks.
 *  • `typography` Tailwind plugin class gives consistent heading / paragraph
 *    defaults across pages.
 */

import React, { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import api from "./api/axiosInstance";
import { useNavVariant } from "./utils/navigation/useNavVariant";
import { ProtectedRoute } from "./components/authentication/ProtectedRoute";

import NavBar from "./components/navigation/NavBar";
import MDNavBar from "./components/navigation/MDNavBar";
import ForgotPasswordRequest from "./components/authentication/ForgotPasswordRequest";
import ResetPassword from "./components/authentication/ResetPassword";

import { firebaseApp } from "./firebase/firebaseApp";
import { getAuth, onIdTokenChanged, getIdTokenResult } from "firebase/auth";
import { useAuth } from "./context/AuthContext";
import PatientProfilePage from "./pages/PatientProfilePage";
import AdminPage from "./pages/AdminPage";
import {
  clearStoredSelectedPatient,
  readStoredSelectedPatient,
  writeStoredSelectedPatient,
} from "./utils/patientProfileSelection";

/* ------------------------------------------------------------------ */
/* Lazy-loaded pages (code-splitting)                                 */
/* ------------------------------------------------------------------ */
const LandingPage = lazy(() => import("./pages/LandingPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const PatientsPage = lazy(() => import("./pages/PatientsPage"));
/* ------------------------------------------------------------------ */

export default function App() {
  const { user, claims } = useAuth(); // grab host’s user
  const [userData, setUserData] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(() =>
    readStoredSelectedPatient(),
  );

  const role = claims?.role;
  const isAdmin =
    ["clinician", "admin"].includes(role) || user?.email == "ben@benwjd.com";
  // const isAdmin = true;

  // Decide which nav bar variant to render based on location auth state.
  const navVariant = useNavVariant(); // 'main' | 'landing'

  useEffect(() => {
    if (selectedPatient) {
      writeStoredSelectedPatient(selectedPatient);
      return;
    }

    clearStoredSelectedPatient();
  }, [selectedPatient]);

  useEffect(() => {
    if (user) {
      return;
    }

    setSelectedPatient(null);
    clearStoredSelectedPatient();
  }, [user]);

  // if role has not been detected yet show a loading screen
  if (role === null && user) {
    return (
      <div className="h-[100dvh] flex items-center justify-center">
        Loading…
      </div>
    );
  }

  return (
    <div className="h-[100dvh] flex flex-col typography relative">
      {navVariant === "main" ? <NavBar /> : <MDNavBar />}

      {/* Suspense boundary – shows fallback while lazy chunks download */}
      <div className="h-full min-h-0 flex md:items-start md:justify-center ">
        <Suspense fallback={<div className="m-auto">Loading…</div>}>
          <Routes>
            {/* ---------- Public routes ---------- */}
            <Route path="/" element={<LandingPage />} />
            <Route
              path="/auth/*"
              // element={<AuthPage />}
              element={<AuthPage />}
            />
            <Route
              path="/auth/forgot-password"
              element={<ForgotPasswordRequest />}
            />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            {/* ---------- Protected routes ---------- */}
            <>
              <Route
                path="/patients"
                element={
                  <ProtectedRoute>
                    <PatientsPage
                      userData={userData}
                      selectedPatient={selectedPatient}
                      setSelectedPatient={setSelectedPatient}
                    />
                  </ProtectedRoute>
                }
              />
            </>
            <>
              <Route
                path="/patients/profile"
                element={
                  <ProtectedRoute>
                    <PatientProfilePage
                      userData={userData}
                      selectedPatient={selectedPatient}
                      setSelectedPatient={setSelectedPatient}
                    />
                  </ProtectedRoute>
                }
              />
            </>

            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  {isAdmin ? <AdminPage /> : <Navigate to="/form" replace />}
                </ProtectedRoute>
              }
            />
            {/* ---------- Fallback ---------- */}
            <Route path="*" element={<Navigate to={"/patients"} replace />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}
